import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Avatar,
  Pagination,
  Box,
  useMediaQuery,
} from "@mui/material";
import { Archive } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import color from "../utils/Colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpFromBracket,
  faPenToSquare,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

type Entity = {
  id?: string;
  profilePic: string;
  name: string;
  email?: string;
  contact?: string;
  feedback?: string;
  subject?: string;
  joiningDate: string;
  verified: boolean;
};

type FieldConfig = {
  label: string;
  fieldName: keyof Entity;
};

type EntityListProps = {
  entities: Entity[];
  listName: string;
  fieldsConfig?: FieldConfig[];
  showAddButton?: boolean;
};

const defaultFieldsConfig: FieldConfig[] = [
  { label: "Email Id", fieldName: "email" },
  { label: "Contact no.", fieldName: "contact" },
];

const EntityList: React.FC<EntityListProps> = ({
  entities,
  listName,
  fieldsConfig = defaultFieldsConfig,
  showAddButton = true
}) => {
  const isSmallScreen = useMediaQuery("(max-width:660px)");

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleEdit = (entity: Entity) => {
    alert(`Editing ${entity.name}`);
  };

  const handleDelete = (entity: Entity) => {
    alert(`Deleting ${entity.name}`);
  };

  const handleExport = (entity: Entity) => {
    alert(`Exporting data for ${entity.name}`);
  };

  const handleArchive = (entity: Entity) => {
    alert(`Archiving ${entity.name}`);
  };

  return (
    <Box
      sx={{
        width: "100%",
        padding: "20px",
        paddingX: 0,
        border: "solid 1.5px",
        borderColor: color.borderColor,
        boxShadow: 2,
        borderRadius: "8px",
        background: "white",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 1.5, sm: 3 },
          py: 2,
          pt: 1,
        }}
      >
        <h2 style={{ color: color.textColor, textAlign: "left" }}>
          {listName} List
        </h2>

        {showAddButton && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            style={{
              float: "right",
              background: color.firstColor,
              textTransform: "none",
              padding: isSmallScreen ? "6px" : "auto",
              fontSize: isSmallScreen ? "12px" : "auto",
              borderRadius: "6px",
              paddingTop: "10px",
              paddingBottom: "10px",
            }}
          >
            Add New {listName}
          </Button>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          borderTop: "solid 1px",
          borderBottom: "solid 1px",
          borderColor: color.borderColor,
          borderRadius: "0px",
        }}
      >
        <Table
          aria-label={`${listName.toLowerCase()} table`}
          sx={{ boxShadow: "none", textAlign: "center" }}
        >
          <TableHead>
            <TableRow>
              <TableCell id="tableCellR">Profile</TableCell>
              <TableCell id="tableCellR">Name</TableCell>
              {fieldsConfig.map((field) => (
                <TableCell key={field.fieldName} id="tableCellR">
                  {field.label}
                </TableCell>
              ))}
              <TableCell id="tableCellR">Joining Date</TableCell>
              <TableCell id="tableCellR">Verified</TableCell>
              <TableCell id="tableCellR">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entities
              .slice((page - 1) * rowsPerPage, page * rowsPerPage)
              .map((entity) => (
                <TableRow key={entity.id || entity.email || entity.name}>
                  <TableCell id="tableCell">
                    <Avatar alt={entity.name} src={entity.profilePic} />
                  </TableCell>
                  <TableCell id="tableCell">{entity.name}</TableCell>
                  {fieldsConfig.map((field) => (
                    <TableCell key={field.fieldName} id="tableCell">
                      {entity[field.fieldName] || "N/A"}
                    </TableCell>
                  ))}
                  <TableCell id="tableCell">{entity.joiningDate}</TableCell>
                  <TableCell id="tableCell">
                    <Box
                      id="verifiedBox"
                      style={{ display: "flex", justifyContent: "center" }}
                    >
                      {entity.verified ? (
                        <CheckCircleIcon style={{ color: "green" }} />
                      ) : (
                        <CancelIcon style={{ color: "red" }} />
                      )}
                      {entity.verified ? " Verified" : " Not Verified"}
                    </Box>
                  </TableCell>
                  <TableCell
                    id="tableCell"
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <IconButton
                      onClick={() => handleEdit(entity)}
                      sx={{ color: color.firstColor, fontSize: "18px" }}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(entity)}
                      sx={{ color: "red", fontSize: "18px" }}
                      aria-label="delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleExport(entity)}
                      sx={{ color: color.firstColor, fontSize: "18px" }}
                    >
                      <FontAwesomeIcon icon={faArrowUpFromBracket} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleArchive(entity)}
                      sx={{ color: color.firstColor }}
                    >
                      <Archive />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        count={Math.ceil(entities.length / rowsPerPage)}
        page={page}
        onChange={handleChangePage}
        style={{ marginTop: "100px", float: "right" }}
      />
    </Box>
  );
};

export default EntityList;
